import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { HelpService } from '../../../services/help.service';
import { HelpTocComponent } from '../help-toc/help-toc.component';
import { HelpChatComponent } from '../help-chat/help-chat.component';
import { HelpChatStore } from '../services/help-chat.store';

type HelpTab = 'toc' | 'chat';

/**
 * The global help panel shown in a split pane next to the routed content.
 * Holds the Contents tree and the AI assistant behind a tab bar.
 */
@Component({
  selector: 'adf-help-panel',
  imports: [AsyncPipe, HelpTocComponent, HelpChatComponent],
  templateUrl: './help-panel.component.html',
  styleUrl: './help-panel.component.scss',
})
export class HelpPanelComponent implements OnInit {
  private readonly _helpService = inject(HelpService);
  private readonly _chatStore = inject(HelpChatStore);
  private readonly _destroyRef = inject(DestroyRef);

  /** Whether the backend offers an AI chat. Without one there is only the contents, and no tab bar. */
  readonly chatEnabled$ = this._chatStore.enabled$;

  activeTab: HelpTab = 'toc';

  ngOnInit(): void {
    // The panel stays mounted (collapsed) for the whole session, so the chat status is checked each
    // time it opens; the store asks the backend only until it has had a successful answer.
    this._helpService.isOpen$
      .pipe(
        filter((open) => open),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => void this._chatStore.loadStatus());

    // A chat that goes away (the AI service stopped answering) takes the reader back to the contents.
    this.chatEnabled$
      .pipe(
        filter((enabled) => !enabled),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => (this.activeTab = 'toc'));
  }

  selectTab(tab: HelpTab): void {
    this.activeTab = tab;
  }

  close(): void {
    this._helpService.close();
  }
}
